import _ from 'lodash';

import { FieldInputType } from 'constants/fields';
import { LoanChangesHistoryEntityType } from 'constants/loanChanges';
import { DEFAULT_DECIMAL_SCALE } from 'constants/number';
import {
  deedRestrictedOptions,
  demographicInformationProvisionTypeOptions,
} from 'constants/options';
import { normalizeViewPhoneNumber } from 'utils/data';
import { jsonParse } from 'utils/json';
import {
  formatNumberToCurrencyString,
  formatNumberToFixed2DecimalsExceptNull,
  formatNumberToFixed3Decimals,
  formatNumberToFixed3DecimalsExceptNull,
} from 'utils/number';
import { formatPhoneNumberForDisplay } from 'utils/phone';

import { LoanChangesHistoryFieldsConfig } from './LoanChangesHistoryFields.config';

// History keeps a phone as stored ("+16165550161"); the page shows it as "(616) 555-0161".
const formatPhoneNumber = value =>
  _.isString(value) ? formatPhoneNumberForDisplay(normalizeViewPhoneNumber(value)) : value;

const fieldCustomFormatByEntityTypeName = {
  [LoanChangesHistoryEntityType.PROPERTY]: {
    otherExpenses: value =>
      jsonParse(value || '[]').reduce(
        (acc, { Name, Value }) =>
          `${acc}${Name}: ${formatNumberToCurrencyString(Value, { isNullishDisallowed: true })}\n`,
        '',
      ),
    deedRestrictionType: value =>
      deedRestrictedOptions.find(option => option.value === value)?.name,
  },
  [LoanChangesHistoryEntityType.LOAN]: {
    ltv: formatNumberToFixed2DecimalsExceptNull,
    cltv: formatNumberToFixed2DecimalsExceptNull,
    hltv: formatNumberToFixed2DecimalsExceptNull,
    tip: formatNumberToFixed2DecimalsExceptNull,
    finalBuyPrice: formatNumberToFixed3Decimals,
    buyPrice: formatNumberToFixed3Decimals,
    programPoints: formatNumberToFixed3DecimalsExceptNull,
    piti: formatNumberToFixed2DecimalsExceptNull,
  },
  [LoanChangesHistoryEntityType.LOAN_CUSTOM_ADJUSTMENT]: {
    amount: formatNumberToFixed3DecimalsExceptNull,
  },
  [LoanChangesHistoryEntityType.LOAN_LOCK_EXTENSION]: {
    price: formatNumberToFixed3DecimalsExceptNull,
  },
  [LoanChangesHistoryEntityType.BORROWER]: {
    demographicInformationProvisionType: value =>
      demographicInformationProvisionTypeOptions.find(option => option.value === value)?.name,
    cellPhoneNumber: formatPhoneNumber,
    homePhoneNumber: formatPhoneNumber,
    workPhoneNumber: formatPhoneNumber,
  },
};

export const loanChangesHistoryFieldsProxy = rootEntityTypeName => {
  const entityFieldsConfig = LoanChangesHistoryFieldsConfig[rootEntityTypeName] || {};

  return new Proxy(entityFieldsConfig, {
    get(target, propertyName, receiver) {
      const customFormat = fieldCustomFormatByEntityTypeName[rootEntityTypeName]?.[propertyName];
      const { inputProps } = target[propertyName] || {};

      const isCustomFormatAppliedToField = _.isFunction(customFormat);
      const isCurrencyFieldType = inputProps?.type === FieldInputType.CURRENCY;

      if (isCustomFormatAppliedToField) {
        return {
          ...target[propertyName],
          inputProps: { ...inputProps, customFormat },
        };
      }

      if (isCurrencyFieldType) {
        return {
          ...target[propertyName],
          inputProps: {
            ...inputProps,
            customFormat: value =>
              formatNumberToCurrencyString(value, {
                maximumFractionDigits: inputProps.decimalScale ?? DEFAULT_DECIMAL_SCALE,
                minimumFractionDigits: inputProps.decimalScale ?? DEFAULT_DECIMAL_SCALE,
                isNullishDisallowed: true,
              }),
          },
        };
      }

      return Reflect.get(target, propertyName, receiver);
    },
  });
};
